import { useEffect, useMemo, useRef, useState } from 'react'

import {
  PlacementActionType,
  usePlacementWriteQueue,
} from './usePlacementWriteQueue'
import { useUpdateDevice } from './useUpdateDevice'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  useFacilityDevicesByFloorsQuery,
  useFacilityFloorPlanQuery,
} from '@/graphql/generated/hooks'
import { DeviceType, UrlContent } from '@/graphql/generated/schemas'
import { selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { DeviceI, DevicePlacementContextI, FloorI, Point } from '../types/types'
import { getCompanionPlacements } from '../utils/associatedDevicePlacement'
import { getIsFloorPlanKnownEmpty } from '../utils/floorPlanAvailability'
import {
  filterDevicesWithAssociations,
  getDeviceFloorIds,
  mapDevices,
  reconcileDevices,
} from '../utils/utils'

const ASSOCIATED_DEVICES_TOAST_DURATION = 5000

export const useFloorData = (
  floorId: string,
  floors: FloorI[],
  facilityId: string,
  isEditMode: boolean,
  types: DeviceType[],
  nameLikeFilter?: string,
  shouldShowAssociatedDevices?: boolean
) => {
  const [imageUrl, setImageUrl] = useState(null)
  const [devices, setDevices] = useState<DeviceI[]>([])
  const [removingDeviceIds, setRemovingDeviceIds] = useState(new Set<string>())
  const dragStartPositionsRef = useRef(new Map<string, Point>())
  const removingDeviceIdsRef = useRef(new Set<string>())
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  const isDevicesPanel = drawerOpen === 'devices'

  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts({
    toastDuration: ASSOCIATED_DEVICES_TOAST_DURATION,
  })
  const { shouldEnableUpdateDevice } = usePermissions()
  const isAssociatedPlacementEnabled =
    useFeatureFlag(FeatureFlag.floorPlanAssociatedDevicePlacement) ||
    useFeatureFlag(FeatureFlag.development)

  const { removeDevicePlacement, updateDevice, updateDevicePositions } =
    useUpdateDevice(floorId)
  const shouldFilterByNameLike = isDevicesPanel && !shouldShowAssociatedDevices
  const {
    data: devicesData,
    loading: isDevicesLoading,
    refetch: refetchDevices,
  } = useFacilityDevicesByFloorsQuery({
    // Switching side-panel tabs re-reads this query. Serving the cache first keeps the
    // canvas on screen — and on the placement the UpdateDevice write put there — while
    // the server revalidates in the background (ENG-31715).
    fetchPolicy: 'cache-and-network',
    skip: facilityId === undefined,
    variables: {
      floorId: floorId,
      facilityId: facilityId,
      types: types,
      floorIds: getDeviceFloorIds(floorId, floors, types),
      ...(shouldFilterByNameLike && { nameLike: nameLikeFilter || undefined }),
    },
  })
  const {
    data: floorPlanData,
    loading: isFloorPlanLoading,
    error: floorPlanError,
  } = useFacilityFloorPlanQuery({
    skip: facilityId === undefined,
    variables: {
      floorId: floorId,
      facilityId: facilityId,
    },
  })

  // The payload names the floor it describes, so a result left over from the previous
  // floor is detectable — count it as loading rather than draw it on the new plan.
  const loadedFloorId = devicesData?.facility?.floors?.edges?.[0]?.node?.id
  const isStaleFloorResult =
    !!devicesData && !!floorId && loadedFloorId !== floorId
  const devicesResult = isStaleFloorResult ? undefined : devicesData

  // `cache-and-network` reports `loading` for a background revalidation too; only a
  // result we cannot use yet should raise the skeleton.
  const isInitialDeviceLoad = isDevicesLoading && !devicesResult

  // A view over the device state, never written back into it. Derived so that toggling
  // the filter cannot clobber a placement the way re-seeding state from the server did.
  const visibleDevices = useMemo(() => {
    if (!(isDevicesPanel && shouldShowAssociatedDevices)) return devices

    return filterDevicesWithAssociations(
      devices,
      nameLikeFilter,
      // Searching for a door only surfaces its cameras once the door side of a connection
      // is read directly, which is what makes search -> drag place associated devices.
      { shouldUseConnectionCameraIds: isAssociatedPlacementEnabled }
    )
  }, [
    devices,
    isDevicesPanel,
    shouldShowAssociatedDevices,
    nameLikeFilter,
    isAssociatedPlacementEnabled,
  ])

  // Derived from the live query result rather than the imageUrl state below,
  // which can hold the previous floor's URL mid-switch (ENG-33744 review).
  const isFloorPlanKnownEmpty = getIsFloorPlanKnownEmpty(
    isFloorPlanLoading,
    floorPlanError,
    floorPlanData
  )

  const onFloorPlanContainerClick = () => {
    setDevices((currentData) =>
      currentData.map((item) => {
        return { ...item, isEditMode: false, editCheckpointPos: false }
      })
    )
  }

  const onDeviceClicked = (deviceId: string) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === deviceId) {
          return {
            ...item,
            isEditMode: !item.isEditMode,
          }
        }
        return { ...item, isEditMode: false, editCheckpointPos: false }
      })
    )
  }

  const onMoveCheckPointClicked = (deviceId: string) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === deviceId) {
          return {
            ...item,
            editCheckpointPos: !item.editCheckpointPos,
          }
        }
        return { ...item, editCheckpointPos: false }
      })
    )
  }

  const onDeviceUpdated = (data: DeviceI, positionValid: boolean) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === data.id) {
          return {
            ...data,
            invalidPosition: !positionValid,
          }
        }
        return item
      })
    )
  }

  const onDeviceDragStart = (data: DeviceI) => {
    dragStartPositionsRef.current.set(data.id, [data.x, data.y])
  }

  const onPlacementWriteError = (
    error: unknown,
    deviceId: string,
    action: PlacementActionType
  ) => {
    Sentry.captureException(error, {
      tags: { action, feature: 'floor_plan', surface: 'floor_plan_canvas' },
      extra: { deviceId },
    })
    showError(t('floors:plan_update_device_error'))
  }

  const placementWrites = usePlacementWriteQueue({
    write: updateDevice,
    onWriteError: onPlacementWriteError,
    shouldSkipDevice: (deviceId) => removingDeviceIdsRef.current.has(deviceId),
  })

  const onDeviceReleased = (data: DeviceI, positionValid: boolean) => {
    const dragStartPosition = dragStartPositionsRef.current.get(data.id)
    dragStartPositionsRef.current.delete(data.id)

    if (!positionValid) {
      setDevices((currentData) =>
        currentData.map((item) => {
          if (item.id === data.id) {
            return {
              ...item,
              ...(dragStartPosition && {
                x: dragStartPosition[0],
                y: dragStartPosition[1],
              }),
              invalidPosition: false,
            }
          }
          return item
        })
      )
      return
    }

    setDevices((currentData) =>
      currentData.map((item) =>
        item.id === data.id ? { ...data, invalidPosition: false } : item
      )
    )
    void placementWrites.enqueue(data, 'move_placement')
  }

  const onRemoveDevicePlacement = async (deviceId: string) => {
    if (!deviceId || removingDeviceIdsRef.current.has(deviceId)) {
      return false
    }

    removingDeviceIdsRef.current.add(deviceId)
    setRemovingDeviceIds((currentIds) => new Set(currentIds).add(deviceId))
    // The queue stops draining while the device is being removed, so a placement queued
    // behind an in-flight write waits here rather than being sent.
    try {
      const pendingWrites = placementWrites.getPendingWrites(deviceId)
      if (pendingWrites.length) {
        await Promise.all(pendingWrites)
      }

      const isRemoved = await removeDevicePlacement(deviceId)
      if (!isRemoved) return false

      // Only moot once the placement is actually gone. Dropped any earlier, a failed
      // removal leaves the canvas on a position the server never received.
      placementWrites.cancelQueued(deviceId)

      setDevices((currentData) =>
        currentData.map((item) =>
          item.id === deviceId
            ? {
                ...item,
                editCheckpointPos: false,
                invalidPosition: false,
                isEditMode: false,
                placed: false,
              }
            : item
        )
      )
      showSuccess(t('floors:plan_remove_device_success'))
      return true
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'remove_placement',
          feature: 'floor_plan',
          surface: 'device_popover',
        },
        extra: { deviceId },
      })
      showError(t('floors:plan_remove_device_error'))
      return false
    } finally {
      removingDeviceIdsRef.current.delete(deviceId)
      setRemovingDeviceIds((currentIds) => {
        const nextIds = new Set(currentIds)
        nextIds.delete(deviceId)
        return nextIds
      })
      // A removal that did not go through keeps the device, so the placement it held
      // back is still owed to the server. A no-op after a successful removal.
      placementWrites.resume(deviceId)
    }
  }

  const onDeviceDropped = (
    draggedDeviceId: string,
    position: Point,
    placementContext?: DevicePlacementContextI
  ) => {
    const [deviceX, deviceY] = position
    const deviceData = visibleDevices.find(
      (device) => device.id === draggedDeviceId
    )

    // Placing associated devices only makes sense on a device's first placement: nudging an
    // already placed device must not drag its unplaced counterparts onto the floor plan again.
    // Without update permission nothing persists, so a toast would claim work that never
    // happened.
    const shouldPlaceAssociatedDevices =
      isAssociatedPlacementEnabled &&
      isDevicesPanel &&
      shouldEnableUpdateDevice &&
      !deviceData?.placed &&
      !!placementContext?.mapImageSize

    const companionPlacements = shouldPlaceAssociatedDevices
      ? getCompanionPlacements({
          devices: visibleDevices,
          draggedDeviceId,
          dropPosition: position,
          iconSize: placementContext.iconSize,
          mapImageSize: placementContext.mapImageSize,
        })
      : []
    const companionsById = new Map(
      companionPlacements.map((placement) => [placement.deviceId, placement])
    )

    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === draggedDeviceId) {
          return {
            ...item,
            x: deviceX,
            y: deviceY,
            isEditMode: true,
            placed: true,
            invalidPosition: false,
            checkpoint: {
              ...item.checkpoint,
              floor: {
                ...item.checkpoint.floor,
                id: floorId,
              },
            },
          }
        }
        const companion = companionsById.get(item.id)
        // Re-check `placed` against the current state rather than the render it was resolved
        // from, so a device placed in the meantime is never moved.
        if (companion && !item.placed) {
          return {
            ...item,
            x: companion.x,
            y: companion.y,
            // a companion camera is turned to face the device it is associated with
            rotation: companion.rotation ?? item.rotation,
            distance: companion.distance ?? item.distance,
            isEditMode: false,
            editCheckpointPos: false,
            placed: true,
            invalidPosition: false,
          }
        }
        return {
          ...item,
          isEditMode: false,
          editCheckpointPos: false,
          invalidPosition: false,
        }
      })
    )
    if (deviceData) {
      void placementWrites.enqueue(
        { ...deviceData, x: deviceX, y: deviceY },
        'place_device'
      )
    }

    if (companionPlacements.length === 0) return

    showSuccess(t('floors:plan_associated_devices_placed_success'))
    trackEvent('Floor Plan - Associated Devices Auto Placed', {
      facility_id: facilityId,
      floor_id: floorId,
      dragged_device_id: draggedDeviceId,
      associated_device_count: companionPlacements.length,
    })
    const companionWrites = updateDevicePositions(
      companionPlacements.map((placement) => {
        const companion = visibleDevices.find(
          (device) => device.id === placement.deviceId
        )
        return {
          ...companion,
          x: placement.x,
          y: placement.y,
          rotation: placement.rotation ?? companion.rotation,
          distance: placement.distance ?? companion.distance,
        }
      })
    )

    // Companions are placed on the canvas the same way the dragged device is, so they
    // need the same ownership — without it a query landing before these writes settle
    // reconciles them back to unplaced. `updateDevicePositions` settles only once every
    // write has, so none is released early.
    placementWrites.trackExternalWrite(
      companionPlacements.map((placement) => placement.deviceId),
      companionWrites
    )

    void companionWrites.then((hasPartialFailure) => {
      if (hasPartialFailure) {
        showError(t('floors:plan_associated_devices_placed_error'))
      }
    })
  }

  // Keyed on the usable result alone: opening a side panel is a view change, not new
  // data, and re-running on `isDevicesPanel` is what reverted a drag (ENG-31715). Not
  // gated on `isDevicesLoading` either — a cached result for a newly selected floor
  // arrives mid-revalidation, and waiting would leave the previous floor on screen.
  useEffect(() => {
    if (!devicesResult) return

    const locallyOwnedIds = new Set([
      ...dragStartPositionsRef.current.keys(),
      ...placementWrites.getPendingDeviceIds(),
      ...removingDeviceIdsRef.current,
    ])

    setDevices((currentData) =>
      reconcileDevices(currentData, mapDevices(devicesResult), locallyOwnedIds)
    )
  }, [devicesResult])

  useEffect(() => {
    if (!isFloorPlanLoading && floorPlanData) {
      const floorPlanImage = floorPlanData?.facility?.floors?.edges[0]?.node
        ?.floorPlan?.image as UrlContent
      setImageUrl(floorPlanImage?.url)
    }
  }, [floorPlanData, isFloorPlanLoading])

  useEffect(() => {
    setDevices((currentData) =>
      currentData.map((item) => ({
        ...item,
        isEditMode: false,
        editCheckpointPos: false,
      }))
    )
  }, [isEditMode])

  const checkpointDevices = useMemo(
    () => visibleDevices?.filter((d) => d.deviceType === DeviceType.Checkpoint),
    [visibleDevices]
  )

  return {
    facilityId,
    imageUrl,
    isFloorPlanKnownEmpty,
    devices: visibleDevices,
    checkpointDevices,
    loading: isInitialDeviceLoad,
    isRemovingDevice: (deviceId?: string) =>
      !!deviceId && removingDeviceIds.has(deviceId),
    onDeviceClicked,
    onDeviceDragStart,
    onFloorPlanContainerClick,
    onDeviceUpdated,
    onDeviceReleased,
    onDeviceDropped,
    onRemoveDevicePlacement,
    onMoveCheckPointClicked,
    refetchDevices,
  }
}
